import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAutomationsOnHoldQuery } from '../../../../queries/Automation/queries/useAutomationsOnHoldQuery';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { AutomationPendingIcon } from '../../svgs/graphics/AutomationPendingIcon';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps {
	className?: string;
	shouldHide?: boolean;
	styles?: StyleDeclarationValue[];
}

const _AutomationsPendingCard: React.FC<IProps> = ({ className, styles, shouldHide, logApiError }) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();

	const canSendOnBehalf = userSession?.account?.features?.automation?.allowAdminToStartOnBehalf || false;
	const automationEnabled = userSession?.account?.features?.automation?.enabled || false;

	const {
		data: myAutomationsOnHold = [],
		isLoading: isMyAutomationsLoading,
		error: myAutomationsError,
	} = useAutomationsOnHoldQuery({
		includeAll: false,
		enabled: automationEnabled && !canSendOnBehalf,
	});

	const {
		data: allAutomationsOnHold = [],
		isLoading: isAllAutomationsLoading,
		error: allAutomationsError,
	} = useAutomationsOnHoldQuery({
		includeAll: true,
		enabled: automationEnabled && canSendOnBehalf,
	});

	const isLoading = canSendOnBehalf ? isAllAutomationsLoading : isMyAutomationsLoading;
	const error = canSendOnBehalf ? allAutomationsError : myAutomationsError;

	React.useEffect(() => {
		if (error) {
			errorMessages.pushApiError(error as Api.IOperationResultNoValue);
			logApiError('LoadAutomationsOnHold', error as Api.IOperationResultNoValue);
		}
	}, [error, errorMessages, logApiError]);

	// Ensure arrays are defined before calling filter to prevent TypeError when data is undefined.
	const myAutomationsArray = Array.isArray(myAutomationsOnHold) ? myAutomationsOnHold : [];
	const allAutomationsArray = Array.isArray(allAutomationsOnHold) ? allAutomationsOnHold : [];

	const automationsToDisplay = canSendOnBehalf ? allAutomationsArray : myAutomationsArray;

	const myAutomationsPending = myAutomationsArray.filter(x => x.count && x.count > 0);
	const allAutomationsPending = canSendOnBehalf
		? allAutomationsArray.filter(x => x.count && x.count > 0)
		: allAutomationsArray.filter(x => x.count && x.count > 0 && x.template?.creator?.id === userSession?.user?.id);

	const shouldHideCard =
		!(myAutomationsPending.length > 0 || allAutomationsPending.length > 0) ||
		isLoading ||
		shouldHide ||
		!automationEnabled;

	const onClickSeePending = () => {
		fullscreenModal.history.push({
			pathname: `/automationsonhold/${Api.ResourceSelectorId.AutomationsOnHold}`,
		});
	};

	if (shouldHideCard) {
		return null;
	}

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} automations-pending-card ${className || ''}`}>
			<figure className={css(styleSheet.iconContainer)}>
				<AutomationPendingIcon withoutRing={true} />
			</figure>
			<span className={css(bs.uppercase, styleSheet.titleText)}>
				<strong className={css(styleSheet.title)}>Pending Approval</strong>
			</span>
			<div className={css(styleSheet.pendingList)}>
				{automationsToDisplay.map(automation => {
					if (automation.count && automation.template) {
						return (
							<div className={css(styleSheet.pendingListItem)} key={automation.template?.id}>
								<span className={css(baseStyleSheet.fontBold)}>{automation.template?.name}</span>
								<span>({automation.count} contacts)</span>
							</div>
						);
					}
					return null;
				})}
			</div>
			<div className={css(styleSheet.ctaContainer)}>
				<button className={css(styleSheet.button)} onClick={onClickSeePending}>
					<span>See Pending</span>
				</button>
			</div>
		</div>
	);
};

export const AutomationsPendingCard = withEventLogging(_AutomationsPendingCard, 'AutomationsPendingCard');
